import type { ResolvedElement } from '../../../types/preview';
import type {
  CanvasElement,
  TextElement,
  ShapeElement,
  ImageElement,
  ButtonElement,
} from '../../../types/index';

interface OverlayRendererProps {
  resolvedElements: ResolvedElement[];
  canvasWidth: number;
  canvasHeight: number;
  zoom: number;
  showBoundaries: boolean;
  showLayerIndicators: boolean;
  transparent: boolean;
}

export default function OverlayRenderer({
  resolvedElements,
  canvasWidth,
  canvasHeight,
  zoom,
  showBoundaries,
  showLayerIndicators,
  transparent,
}: OverlayRendererProps) {
  const overlayElements = resolvedElements
    .filter((re) => re.resolved.layer === 'OVERLAY_LAYER' && re.resolved.visible)
    .sort((a, b) => a.resolved.zIndex - b.resolved.zIndex);

  return (
    <div
      className={`relative overflow-hidden ${transparent ? '' : 'bg-gray-900'}`}
      style={{
        width: canvasWidth * zoom,
        height: canvasHeight * zoom,
        backgroundColor: transparent ? 'transparent' : '#111827',
      }}
      role="region"
      aria-label="Overlay preview — accessible content layer"
    >
      {!transparent && (
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(45deg, #374151 25%, transparent 25%),
              linear-gradient(-45deg, #374151 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #374151 75%),
              linear-gradient(-45deg, transparent 75%, #374151 75%)
            `,
            backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
            backgroundPosition: `0 0, 0 ${10 * zoom}px, ${10 * zoom}px -${10 * zoom}px, -${10 * zoom}px 0`,
          }}
        />
      )}

      {overlayElements.map((re) => (
        <OvElementRenderer
          key={re.resolved.id}
          element={re.resolved}
          zoom={zoom}
          showBoundary={showBoundaries}
          showLayerIndicator={showLayerIndicators}
          fullyResolved={re.fullyResolved}
        />
      ))}

      {overlayElements.length === 0 && !transparent && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-gray-400">No overlay elements</p>
            <p className="text-xs text-gray-500 mt-1">
              Assign elements to OVERLAY_LAYER for ADA-compliant output
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

interface OvElementRendererProps {
  element: CanvasElement;
  zoom: number;
  showBoundary: boolean;
  showLayerIndicator: boolean;
  fullyResolved: boolean;
}

function OvElementRenderer({
  element,
  zoom,
  showBoundary,
  showLayerIndicator,
  fullyResolved,
}: OvElementRendererProps) {
  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: element.x * zoom,
    top: element.y * zoom,
    width: element.width * zoom,
    height: element.height * zoom,
    opacity: element.opacity,
    transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
    transformOrigin: 'center center',
    outline: showBoundary ? '1px dashed rgba(6, 182, 212, 0.5)' : undefined,
    boxShadow: showLayerIndicator
      ? 'inset 0 0 0 2px rgba(6, 182, 212, 0.5)'
      : undefined,
  };

  const unresolvedOutline: React.CSSProperties = !fullyResolved
    ? { outline: '2px dashed rgba(239, 68, 68, 0.5)' }
    : {};

  switch (element.type) {
    case 'text':
      return <OvText element={element} style={{ ...baseStyle, ...unresolvedOutline }} zoom={zoom} />;
    case 'shape':
      return <OvShape element={element} style={{ ...baseStyle, ...unresolvedOutline }} zoom={zoom} />;
    case 'image':
      return <OvImage element={element} style={{ ...baseStyle, ...unresolvedOutline }} zoom={zoom} />;
    case 'button':
      return <OvButton element={element} style={{ ...baseStyle, ...unresolvedOutline }} zoom={zoom} />;
    default:
      return null;
  }
}

function OvText({
  element,
  style,
  zoom,
}: {
  element: TextElement;
  style: React.CSSProperties;
  zoom: number;
}) {
  return (
    <p
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent:
          element.textAlign === 'center'
            ? 'center'
            : element.textAlign === 'right'
            ? 'flex-end'
            : 'flex-start',
        fontFamily: element.fontFamily || 'Inter, sans-serif',
        fontSize: (element.fontSize || 16) * zoom,
        fontWeight: element.fontWeight || 400,
        fontStyle: element.fontStyle || 'normal',
        textDecoration: element.textDecoration || 'none',
        textTransform: (element.textTransform || 'none') as React.CSSProperties['textTransform'],
        lineHeight: element.lineHeight || 1.4,
        letterSpacing: element.letterSpacing ? `${element.letterSpacing * zoom}px` : undefined,
        color: element.color || '#000000',
        margin: 0,
        overflow: 'hidden',
      }}
    >
      {element.text || 'Text'}
    </p>
  );
}

function OvShape({
  element,
  style,
  zoom,
}: {
  element: ShapeElement;
  style: React.CSSProperties;
  zoom: number;
}) {
  if (element.shapeType === 'line') {
    return (
      <div
        style={{
          ...style,
          height: Math.max((element.strokeWidth || 1) * zoom, 1),
          backgroundColor: element.stroke || element.fill || '#000000',
        }}
        role="presentation"
        aria-hidden="true"
      />
    );
  }

  const shapeStyle: React.CSSProperties = {
    ...style,
    backgroundColor: element.fill || 'transparent',
    opacity: (element.opacity ?? 1) * (element.fillOpacity ?? 1),
    border: element.stroke && element.stroke !== 'transparent'
      ? `${(element.strokeWidth || 1) * zoom}px solid ${element.stroke}`
      : undefined,
  };

  if (element.shapeType === 'circle') {
    shapeStyle.borderRadius = '50%';
  } else if (element.shapeType === 'rounded-rect') {
    shapeStyle.borderRadius = `${(element.borderRadius || 8) * zoom}px`;
  }

  return <div style={shapeStyle} role="presentation" aria-hidden="true" />;
}

function OvImage({
  element,
  style,
  zoom,
}: {
  element: ImageElement;
  style: React.CSSProperties;
  zoom: number;
}) {
  if (!element.src) {
    return (
      <div
        style={{
          ...style,
          backgroundColor: '#1f2937',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        role="img"
        aria-label={element.alt || 'Image placeholder'}
      >
        <span style={{ fontSize: 11 * zoom, color: 'rgba(255,255,255,0.3)' }}>
          No image
        </span>
      </div>
    );
  }

  return (
    <div style={{ ...style, overflow: 'hidden' }}>
      <img
        src={element.src}
        alt={element.alt || ''}
        style={{
          width: '100%',
          height: '100%',
          objectFit: element.objectFit || 'cover',
        }}
      />
    </div>
  );
}

function OvButton({
  element,
  style,
  zoom,
}: {
  element: ButtonElement;
  style: React.CSSProperties;
  zoom: number;
}) {
  const Tag = element.href ? 'a' : 'span';

  return (
    <Tag
      href={element.href || undefined}
      target={element.href ? '_blank' : undefined}
      rel={element.href ? 'noopener noreferrer' : undefined}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: element.backgroundColor || '#a855f7',
        color: element.color || '#ffffff',
        borderRadius: `${(element.borderRadius || 4) * zoom}px`,
        fontSize: (element.fontSize || 14) * zoom,
        fontWeight: element.fontWeight || 600,
        fontFamily: element.fontFamily || 'Inter, sans-serif',
        paddingLeft: `${(element.paddingX || 16) * zoom}px`,
        paddingRight: `${(element.paddingX || 16) * zoom}px`,
        paddingTop: `${(element.paddingY || 8) * zoom}px`,
        paddingBottom: `${(element.paddingY || 8) * zoom}px`,
        textDecoration: 'none',
        cursor: element.href ? 'pointer' : 'default',
      }}
      role="link"
      aria-label={element.text || 'Button'}
    >
      {element.text || 'Button'}
    </Tag>
  );
}
